import React, { useCallback, useState } from 'react';

import './Accordion.css';

export interface AccordionItem {
  /**
   * Unique identifier for the accordion item
   */
  id: string;
  /**
   * Title text displayed in the accordion header
   */
  title: string;
  /**
   * Whether this accordion item is required (displays asterisk)
   */
  required?: boolean;
  /**
   * Content to render inside the accordion panel when expanded
   */
  content: React.ReactNode;
  /**
   * Optional icon to display before the title
   */
  icon?: React.ReactNode;
}

export interface AccordionProps {
  /**
   * Array of accordion items to render
   */
  items: AccordionItem[];
  /**
   * Whether multiple accordion items can be open at the same time
   * @default false
   */
  allowMultipleOpen?: boolean;
  /**
   * Whether all accordion items should be open by default
   * @default false
   */
  defaultAllOpen?: boolean;
  /**
   * Additional CSS class name for the accordion container
   */
  className?: string;
  /**
   * Array of item IDs that should be open by default
   */
  defaultOpenItems?: string[];
  /**
   * Item IDs held open regardless of the user's toggles — a section whose required
   * content is missing after a rejected submit. Closed content is `hidden`, so it could
   * not take focus; deriving this in render means the section is already open when the
   * workflow moves focus into it (ACP-2092).
   */
  forceOpenItems?: string[];
  /**
   * Optional callback invoked when an item is toggled
   */
  onToggle?: (itemId: string, isOpen: boolean) => void;
  /**
   * Test ID for the accordion container
   */
  'data-testid'?: string;
}

interface AccordionItemProps {
  /**
   * The accordion item data to render
   */
  item: AccordionItem;
  /**
   * Whether this accordion item is currently expanded
   */
  isOpen: boolean;
  /**
   * Callback when the accordion item header is clicked
   */
  onToggle: () => void;
}

function AccordionItemComponent({ item, isOpen, onToggle }: AccordionItemProps) {
  const requiredLabel = item.required ? ', required' : '';
  const expandedLabel = isOpen ? 'collapse' : 'expand';
  const ariaLabel = `${expandedLabel} ${item.title}${requiredLabel}`;

  return (
    <div
      className={`accordion-item ${isOpen ? 'accordion-item--open' : ''}`}
      data-testid={`accordion-item-${item.id}`}>
      <h2 className='accordion-item__heading'>
        <button
          aria-controls={`accordion-content-${item.id}`}
          aria-expanded={isOpen}
          aria-label={ariaLabel}
          className='accordion-item__header'
          data-testid={`accordion-item-toggle-${item.id}`}
          id={`accordion-header-${item.id}`}
          type='button'
          onClick={onToggle}>
          <span className='accordion-item__header-content'>
            {item.icon && (
              <span
                aria-hidden='true'
                className='accordion-item__status-icon'>
                {item.icon}
              </span>
            )}
            <span className='accordion-item__title'>
              {item.title}
              {item.required && (
                <span
                  aria-label='required'
                  className='accordion-item__required'>
                  *
                </span>
              )}
            </span>
          </span>
          <span
            aria-hidden='true'
            className='accordion-item__icon'>
            {isOpen ? '−' : '+'}
          </span>
        </button>
      </h2>
      <div
        aria-labelledby={`accordion-header-${item.id}`}
        className='accordion-item__content-wrapper'
        data-testid={`accordion-item-panel-${item.id}`}
        hidden={!isOpen}
        id={`accordion-content-${item.id}`}
        role='region'>
        <div className='accordion-item__content'>{item.content}</div>
      </div>
    </div>
  );
}

export function Accordion({
  items,
  allowMultipleOpen = false,
  defaultAllOpen = false,
  className = '',
  defaultOpenItems = [],
  forceOpenItems,
  onToggle,
  'data-testid': dataTestId,
}: AccordionProps) {
  const [openItems, setOpenItems] = useState<Set<string>>(() => {
    if (defaultAllOpen) {
      return new Set(items.map((item) => item.id));
    }

    return new Set([...defaultOpenItems, ...(forceOpenItems ?? [])]);
  });

  // A section opened because of an error stays open once the error is fixed — otherwise
  // uploading the missing document collapsed it, hiding the new file and the focused
  // upload area. Adopted during render (not in an effect) so the section is already open
  // in the same commit the workflow moves focus into it.
  const forcedKey = (forceOpenItems ?? []).join('|');
  const [adoptedForcedKey, setAdoptedForcedKey] = useState(forcedKey);
  if (forcedKey !== adoptedForcedKey) {
    setAdoptedForcedKey(forcedKey);
    if (forceOpenItems && forceOpenItems.length > 0) {
      setOpenItems((previous) => new Set([...previous, ...forceOpenItems]));
    }
  }

  const handleToggle = useCallback(
    (itemId: string) => {
      // While an error holds a section open, a toggle would only drop it from the user's
      // own state — invisible now, but the section would collapse the moment the error
      // clears, hiding the file just uploaded. Ignore it until the force ends.
      if (forceOpenItems?.includes(itemId)) return;

      const newSet = new Set(openItems);
      let nextIsOpen: boolean;

      if (newSet.has(itemId)) {
        newSet.delete(itemId);
        nextIsOpen = false;
      } else {
        if (!allowMultipleOpen) {
          newSet.clear();
        }
        newSet.add(itemId);
        nextIsOpen = true;
      }

      setOpenItems(newSet);

      onToggle?.(itemId, nextIsOpen);
    },
    [allowMultipleOpen, forceOpenItems, onToggle, openItems],
  );

  return (
    <div
      className={`accordion ${className}`}
      data-testid={dataTestId ?? 'accordion'}>
      {items.map((item) => (
        <AccordionItemComponent
          key={item.id}
          isOpen={openItems.has(item.id) || Boolean(forceOpenItems?.includes(item.id))}
          item={item}
          onToggle={() => handleToggle(item.id)}
        />
      ))}
    </div>
  );
}

export default Accordion;
